class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.drawerClass = "wt-drawer-search";
    this.drawer = this;
    this.classDrawerActive = `${this.drawerClass}--active`;
    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-header__search-trigger",
      ".wt-header__search__close",
      ".search-results__terms",
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);

    // search stuff
    this.cachedResults = {};
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector(".wt-header__search__clear-button");
    this.closeButton = this.querySelector(".wt-header__search__close");
    this.searchResultsContainer = this.querySelector(
      ".wt-header__search__results",
    );
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.searchProperties = this.dataset.searchProperties;

    this.mainTrigger = this.querySelector(".wt-header__search-trigger");
    this.emptyAnnouncement = this.querySelector(".search-empty");
    this.searchResultTop = this.querySelector(".search-result-top");

    this.isVisibleClearButton = false;

    this.saveSuggestionMenuInDesignMode =
      this.saveSuggestionMenuInDesignMode.bind(this);
    this.updateSearchResultsHeight =
      this.updateSearchResultsHeight.bind(this);

    this.isPredictiveSearchAvailable = this.hidePredictiveSearch();
    this.isPredictiveSearchEnabled = this.dataset.enablePredictiveSearch === 'true';

    this.setupEventListeners();

    this.toggleTabindexElements = [this.input, this.closeButton];

    this.suggestionMenuContent = this.querySelector(".search-result-top").cloneNode(true);
    this.timeOut = null;
    this.searchResultsHeightFrame = null;
    this.init();
  }

  connectedCallback() {
    this.initializeBackdrop();
    window.addEventListener("resize", this.updateSearchResultsHeight);
    window.visualViewport?.addEventListener(
      "resize",
      this.updateSearchResultsHeight,
    );
    window.visualViewport?.addEventListener(
      "scroll",
      this.updateSearchResultsHeight,
    );
  }

  disconnectedCallback() {
    this.backdrop?.remove();
    if (this.isOpen) {
      this.body.classList.remove(this.activeOverlayBodyClass);
    }
    window.removeEventListener("resize", this.updateSearchResultsHeight);
    window.visualViewport?.removeEventListener(
      "resize",
      this.updateSearchResultsHeight,
    );
    window.visualViewport?.removeEventListener(
      "scroll",
      this.updateSearchResultsHeight,
    );
    cancelAnimationFrame(this.searchResultsHeightFrame);
  }

  initializeBackdrop() {
    if (this.backdrop) return;

    this.backdrop = document.createElement("div");
    this.backdrop.className = "search-drawer-backdrop";
    this.backdrop.setAttribute("aria-hidden", "true");
    this.body.appendChild(this.backdrop);

    this.backdrop.addEventListener("click", () => {
      if (this.isOpen) this.toggleDrawerClasses();
    });
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input:not([type='hidden']), select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    return {
      focusableElements,
      first: focusableElements()[0],
      last: focusableElements()[focusableElements().length - 1],
    };
  }

  // fixOverlayIssue() {
  //     const body = this.body;
  //
  //     if(body.classList.contains(this.activeOverlayBodyClass)) {
  //         const offsetTop = -parseInt(body.style.top);
  //         body.style.position = 'initial';
  //         body.style.top = 'initial';
  //         body.style.left = 'initial';
  //         window.scrollTo(0, offsetTop);
  //
  //     } else {
  //         // body.style.top = -document.documentElement.scrollTop+'px';
  //         // body.style.left = '0px';
  //         // body.style.position = 'fixed';
  //     }
  // }

  onToggle() {
    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      setTabindex(this.toggleTabindexElements, "-1");
      setTabindex([this.mainTrigger], "0");
      this.isOpen = false;
      setTimeout(() => {
          this.mainTrigger.focus();
        }, 0);
    } else {
      this.setAttribute("open", "");
      setTabindex(this.toggleTabindexElements, "0");
      setTabindex([this.mainTrigger], "-1");
      this.input.focus();
      this.input.setSelectionRange(
        this.input.value.length,
        this.input.value.length,
      );
      this.isOpen = true;
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    // this.fixOverlayIssue();
    this.drawer.classList.toggle(this.classDrawerActive);
    this.body.classList.toggle(this.activeOverlayBodyClass);
    this.backdrop?.classList.toggle(
      "search-drawer-backdrop--active",
      this.isOpen,
    );

    if (this.isOpen) {
      this.scheduleSearchResultsHeightUpdate();
    } else {
      this.resetSearchResultsHeight();
    }
  }

  scheduleSearchResultsHeightUpdate() {
    cancelAnimationFrame(this.searchResultsHeightFrame);
    this.searchResultsHeightFrame = requestAnimationFrame(() => {
      this.updateSearchResultsHeight();
    });
  }

  updateSearchResultsHeight() {
    if (!this.isOpen || window.innerWidth >= 1200) {
      this.resetSearchResultsHeight();
      return;
    }

    const viewport = window.visualViewport;
    const visibleBottom = viewport
      ? viewport.offsetTop + viewport.height
      : window.innerHeight;
    const resultsTop = this.searchResultsContainer.getBoundingClientRect().top;
    const drawerBottomPadding =
      parseFloat(getComputedStyle(this.drawer).paddingBottom) || 0;
    const availableHeight = Math.max(
      0,
      Math.floor(visibleBottom - resultsTop - drawerBottomPadding),
    );

    this.searchResultsContainer.style.setProperty(
      "--search-results-mobile-height",
      `${availableHeight}px`,
    );
  }

  resetSearchResultsHeight() {
    this.searchResultsContainer.style.removeProperty(
      "--search-results-mobile-height",
    );
  }

  init() {
    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.clearButton.addEventListener("click", () => {
      this.input.value = "";
      this.clearButton.style.display = "none";
      this.isVisibleClearButton = false;
      this.clearResults();
    });

    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (e.key === "Escape" || e.keyCode === 27 || e.code === "Escape") {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (isTabPressed) {
        if (this.isOpen) {
          if (e.shiftKey && document.activeElement === first) {
            last.focus();
            e.preventDefault();
          } else if (!e.shiftKey && document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      }
    });

    this.input.addEventListener("input", () => {
      if (this.input.value.length > 0 && !this.isVisibleClearButton) {
        this.clearButton.style.display = "flex";
        this.clearButton.setAttribute("tabindex", "0");
        this.isVisibleClearButton = true;
      } else if (this.input.value.length === 0 && this.isVisibleClearButton) {
        this.clearButton.style.display = "none";
        this.clearButton.setAttribute("tabindex", "-1");
        this.isVisibleClearButton = false;
      }
    });
  }

  hidePredictiveSearch() {
    // when predictive search is not available, we need to hide the predictive search results
    const featuresTag = document.getElementById("shopify-features");
    if (featuresTag) {
      const featuresData = JSON.parse(featuresTag.textContent);
      if (featuresData.predictiveSearch === false) {
        return false;
      }
    }
    return true;
  }

  // search stuff
  setupEventListeners() {
    const form = this.querySelector("form.store-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));
    if (this.isPredictiveSearchAvailable && this.isPredictiveSearchEnabled) {
      this.input.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    if (Shopify.designMode) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode,
      );
    }
  }

  saveSuggestionMenuInDesignMode() {
    if (this.body.classList.contains(this.activeOverlayBodyClass))
      this.drawer.classList.toggle(this.classDrawerActive);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length) {
      this.getSearchResults(searchTerm);
      this.removeAttribute("empty");
    } else {
      this.clearResults();
    }
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    // depending on the site, toggle page on or off in resources[type]
    // setting theme.liquid flag / predictive-search.liquid flag to TRUE, allows this to run; otherwise we do nothing
    const site = document.body.getAttribute( "data-site" );
    const useOverride = document.body.getAttribute( "data-use-search-override" );
    let addOnParam = "";

    if ( useOverride == "true" ) {
      if ( site === "hero" ) {
        addOnParam = ",page";
      }
    } else {
      addOnParam = ",page";
    }

    // disable cache -- this will cause issues when site swapping
    // if (this.cachedResults[queryKey]) {
    //   this.renderSearchResults(this.cachedResults[queryKey]);
    //   return;
    // }

    const isHeroSite = site === "hero";
    const requestSearchTerm = searchTerm;
    const searchProperties = new Set(
      (this.searchProperties || "").split(",").filter(Boolean),
    );
    if (isHeroSite) searchProperties.add("tag");

    const requestUrl = `${routes.predictive_search_url}?q=${encodeURIComponent(requestSearchTerm)}&${encodeURIComponent("resources[type]")}=product,article,collection,query${addOnParam}&${encodeURIComponent("resources[limit_scope]")}=each&${encodeURIComponent("resources[limit]")}=6&${encodeURIComponent("resources[options][fields]")}=${encodeURIComponent(Array.from(searchProperties).join(","))}&section_id=predictive-search`;

    fetch(requestUrl)
      .then((response) => {
        if (!response.ok) {
          let error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const responseDocument = new DOMParser().parseFromString(
          text,
          "text/html",
        );
        const responseSection = responseDocument.querySelector(
          "#shopify-section-predictive-search",
        );

        if (!responseSection) {
          return;
        }

        const resultsMarkup = responseSection.innerHTML;
        // this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    this.timeOut = setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.scheduleSearchResultsHeightUpdate();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
  }

  clearResults() {
    this.input.value = "";
    this.removeAttribute("results");
    this.setAttribute("empty", true);
    this.statusElement.innerHTML = this.suggestionMenuContent.innerHTML || '';

    clearTimeout(this.timeOut);
    setTimeout(() => {
      this.statusElement.removeAttribute("aria-hidden");
      this.scheduleSearchResultsHeightUpdate();
    }, 300);
  }
}

customElements.define("search-drawer", DrawerSearchSection);
